const DEFAULT_SEGMENTS = 12;
const QUOTE_INTERVAL_SEC = 60;

export interface RhSyncCountdown {
  remainingSec: number;
  totalSec: number;
  enabled: boolean;
  intervalMinutes: number;
}

function polar(cx: number, cy: number, r: number, deg: number): { x: number; y: number } {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function donutSegmentPath(
  cx: number,
  cy: number,
  rInner: number,
  rOuter: number,
  startDeg: number,
  endDeg: number,
): string {
  const span = endDeg - startDeg;
  const large = span > 180 ? 1 : 0;
  const o0 = polar(cx, cy, rOuter, startDeg);
  const o1 = polar(cx, cy, rOuter, endDeg);
  const i1 = polar(cx, cy, rInner, endDeg);
  const i0 = polar(cx, cy, rInner, startDeg);
  return [
    `M ${o0.x.toFixed(2)} ${o0.y.toFixed(2)}`,
    `A ${rOuter} ${rOuter} 0 ${large} 1 ${o1.x.toFixed(2)} ${o1.y.toFixed(2)}`,
    `L ${i1.x.toFixed(2)} ${i1.y.toFixed(2)}`,
    `A ${rInner} ${rInner} 0 ${large} 0 ${i0.x.toFixed(2)} ${i0.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

/** Derive RH sync countdown from settings interval × last snapshot refresh (same as server). */
export function computeRhSyncCountdown(
  snapshotRefreshedAt: string | null | undefined,
  intervalMinutes: number,
  enabled: boolean,
  now = Date.now(),
): RhSyncCountdown {
  const interval = Math.max(1, Math.round(intervalMinutes));
  const totalSec = interval * 60;
  if (!enabled) {
    return { remainingSec: totalSec, totalSec, enabled: false, intervalMinutes: interval };
  }
  const lastMs = snapshotRefreshedAt ? Date.parse(snapshotRefreshedAt) : Number.NaN;
  if (!Number.isFinite(lastMs)) {
    return { remainingSec: 0, totalSec, enabled: true, intervalMinutes: interval };
  }
  const dueMs = lastMs + interval * 60_000;
  const remainingSec = Math.max(0, Math.ceil((dueMs - now) / 1000));
  return { remainingSec, totalSec, enabled: true, intervalMinutes: interval };
}

export function rhRefreshCountdownRingFilledCount(
  remainingSec: number,
  totalSec: number,
  segments = DEFAULT_SEGMENTS,
): number {
  const total = Math.max(1, totalSec);
  const remaining = Math.max(0, remainingSec);
  const elapsed = total - remaining;
  return Math.min(segments, Math.floor((elapsed / total) * segments));
}

function formatExpiresIn(sec: number): string {
  if (sec >= 3600) return `${Math.ceil(sec / 3600)}h`;
  if (sec >= 60) return `${Math.ceil(sec / 60)}m`;
  return `${Math.max(0, sec)}s`;
}

export function rhRefreshCountdownTip(opts: {
  sync: RhSyncCountdown;
  quoteRemainingSec: number;
  quoteIntervalSec?: number;
}): string {
  const quoteEvery = Math.max(1, Math.round((opts.quoteIntervalSec ?? QUOTE_INTERVAL_SEC) / 60));
  const quote = `Live quotes every ${quoteEvery}m · next in ${formatExpiresIn(opts.quoteRemainingSec)}`;
  if (!opts.sync.enabled) {
    return `${quote} · Robinhood auto-sync is off`;
  }
  return `${quote} · Robinhood sync every ${opts.sync.intervalMinutes}m · next in ${formatExpiresIn(opts.sync.remainingSec)}`;
}

export function rhRefreshCountdownRingHtml(opts: {
  sync: RhSyncCountdown;
  segments?: number;
}): string {
  const segments = opts.segments ?? DEFAULT_SEGMENTS;
  const filled = opts.sync.enabled
    ? rhRefreshCountdownRingFilledCount(opts.sync.remainingSec, opts.sync.totalSec, segments)
    : 0;
  const cx = 12;
  const cy = 12;
  const rOuter = 10;
  const rInner = 7;
  const gap = 2.5;
  const segSpan = 360 / segments;
  const disabledClass = opts.sync.enabled ? "" : " is-disabled";
  const paths: string[] = [];
  for (let i = 0; i < segments; i++) {
    const start = i * segSpan + gap / 2;
    const end = (i + 1) * segSpan - gap / 2;
    const active = i < filled;
    paths.push(
      `<path class="sfe-rh-refresh-ring-seg${active ? " is-active" : ""}${disabledClass}" d="${donutSegmentPath(cx, cy, rInner, rOuter, start, end)}" />`,
    );
  }
  return `<svg class="sfe-rh-refresh-ring${disabledClass}" viewBox="0 0 24 24" aria-hidden="true">${paths.join("")}</svg>`;
}

export function updateRhRefreshCountdownRing(
  host: HTMLElement,
  opts: {
    sync: RhSyncCountdown;
    quoteRemainingSec: number;
    quoteIntervalSec?: number;
    segments?: number;
  },
): void {
  const segments = opts.segments ?? DEFAULT_SEGMENTS;
  const filled = opts.sync.enabled
    ? rhRefreshCountdownRingFilledCount(opts.sync.remainingSec, opts.sync.totalSec, segments)
    : 0;
  host.querySelectorAll<SVGElement>(".sfe-rh-refresh-ring-seg").forEach((el, index) => {
    el.classList.toggle("is-active", index < filled);
    el.classList.toggle("is-disabled", !opts.sync.enabled);
  });
  host.querySelector(".sfe-rh-refresh-ring")?.classList.toggle("is-disabled", !opts.sync.enabled);
  const tip = rhRefreshCountdownTip(opts);
  host.title = tip;
  host.setAttribute("aria-label", tip);
}

export function flashRhRefreshCountdownRing(host: HTMLElement): void {
  host.querySelectorAll<SVGElement>(".sfe-rh-refresh-ring-seg").forEach((el) => {
    el.classList.remove("is-active");
  });
  host.classList.remove("sfe-rh-refresh-ring-host--reset");
  void host.offsetWidth;
  host.classList.add("sfe-rh-refresh-ring-host--reset");
  window.setTimeout(() => host.classList.remove("sfe-rh-refresh-ring-host--reset"), 700);
}

// Survives rebinds. Mobile recreates the ring host on each paint; resetting
// these inside bind() made a completed countdown fire again every second.
let quoteRefreshQueued = false;
let lastQuoteDueAt = 0;
let pollQueued = false;
let lastPollAt = 0;
let lastSyncRemainingSec: number | null = null;

export function bindRhFeedCountdownRing(
  host: ParentNode,
  options: {
    getPanelFetchedAt: () => number;
    quoteCacheMs: number;
    getRhSyncCountdown: () => RhSyncCountdown;
    onQuoteRefreshDue: () => void;
    onPollRhSnapshot: () => void;
    onRhSyncDue?: () => void;
  },
): () => void {
  const ringHost = host.querySelector<HTMLElement>("[data-rh-refresh-ring]");
  if (!ringHost) return () => {};

  let primed = false;

  const tick = (): void => {
    const quoteRemaining = Math.max(
      0,
      Math.ceil((options.getPanelFetchedAt() + options.quoteCacheMs - Date.now()) / 1000),
    );
    const sync = options.getRhSyncCountdown();
    updateRhRefreshCountdownRing(ringHost, {
      sync,
      quoteRemainingSec: quoteRemaining,
      quoteIntervalSec: options.quoteCacheMs / 1000,
    });

    // First tick only paints the ring. Firing due callbacks here retriggers a
    // full render/rebind loop on mobile, where the host is recreated every paint.
    if (!primed) {
      primed = true;
      lastSyncRemainingSec = sync.remainingSec;
      if (quoteRemaining > 0) quoteRefreshQueued = false;
      return;
    }

    if (quoteRemaining <= 0 && !quoteRefreshQueued && Date.now() - lastQuoteDueAt >= options.quoteCacheMs) {
      quoteRefreshQueued = true;
      lastQuoteDueAt = Date.now();
      options.onQuoteRefreshDue();
    }
    if (quoteRemaining > 0) quoteRefreshQueued = false;

    if (
      sync.enabled &&
      lastSyncRemainingSec !== null &&
      lastSyncRemainingSec > 0 &&
      sync.remainingSec === 0
    ) {
      options.onRhSyncDue?.();
    }
    lastSyncRemainingSec = sync.remainingSec;

    const pollIntervalMs = sync.remainingSec <= 30 ? 10_000 : 30_000;
    if (
      sync.enabled &&
      sync.remainingSec <= 30 &&
      Date.now() - lastPollAt >= pollIntervalMs &&
      !pollQueued
    ) {
      pollQueued = true;
      lastPollAt = Date.now();
      options.onPollRhSnapshot();
      window.setTimeout(() => {
        pollQueued = false;
      }, pollIntervalMs - 500);
    }
  };

  tick();
  const timer = window.setInterval(tick, 1000);
  return () => window.clearInterval(timer);
}
